import { useMemo } from "react";
// Note: useAuth import removed as it's not currently used
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import { Player, Shortlist } from "@/lib/types";

// Activity item type
interface ActivityItem {
  id: string;
  type: "player_added" | "player_shortlisted" | "email_sent" | "database_update";
  title: string;
  subtitle: string;
  timestamp: Date;
  iconBgColor: string;
  iconColor: string;
  icon: string;
}

export default function RecentActivity() {
  // Use data coordinator for unified data access
  const { players, shortlists, emails } = useDataCoordinator();
  
  // Compute activities directly with useMemo - no need for separate state
  const activities = useMemo(() => {
    if (!players || !shortlists || !emails) return [];
    
    const realActivities: ActivityItem[] = [];
    
    // Generate activities from players
    if (players.length > 0) {
      // Sort by date added
      const sortedPlayers = [...players].sort((a, b) => 
        new Date(b.date_added).getTime() - new Date(a.date_added).getTime()
      );
      
      // Take most recent 5
      sortedPlayers.slice(0, 5).forEach(player => {
        realActivities.push({
          id: `player_${player.id}`,
          type: "player_added",
          title: `New player added: ${player.name}`,
          subtitle: `${player.position && player.position.length > 0 ? player.position.join(", ") : "Unknown"} • ${player.age} years old • ${player.nationality || 'Unknown'}`,
          timestamp: new Date(player.date_added),
          iconBgColor: "bg-blue-100",
          iconColor: "text-blue-600",
          icon: "fas fa-user-plus"
        });
      });
    }
    
    // Generate activities from shortlists and shortlist-player connections
    if (shortlists.length > 0) {
      shortlists.forEach((shortlist: Shortlist) => {
        if (shortlist.players && shortlist.players.length > 0) {
          // Get all players in this shortlist
          shortlist.players.forEach((shortlistPlayer: any) => {
            const player = players.find((p: Player) => String(p.id) === String(shortlistPlayer.playerId || shortlistPlayer.id));
            if (player) {
              realActivities.push({
                id: `shortlist_${shortlist.id}_${player.id}`,
                type: "player_shortlisted",
                title: `Added to shortlist: ${player.name}`,
                subtitle: `Added to "${shortlist.name}" • ${player.position && player.position.length > 0 ? player.position.join(", ") : "Unknown"} • ${player.nationality || 'Unknown'}`,
                timestamp: new Date(shortlistPlayer.date_added || Date.now()),
                iconBgColor: "bg-green-100",
                iconColor: "text-green-600",
                icon: "fas fa-star"
              });
            }
          });
        }
      });
    }
    
    // Generate activities from emails
    if (emails.length > 0 && players.length > 0) {
      // Take most recent 3 emails
      const sortedEmails = [...emails].sort((a, b) => 
        new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
      ).slice(0, 3);
      
      sortedEmails.forEach(email => {
        // Find the player for this email
        const emailPlayer = players.find(p => String(p.id) === String(email.player_id));
        
        if (emailPlayer) {
          realActivities.push({
            id: `email_${email.id}`,
            type: "email_sent",
            title: `Email sent to: ${emailPlayer.name}`,
            subtitle: `${email.subject || 'No subject'} • ${emailPlayer.position && emailPlayer.position.length > 0 ? emailPlayer.position.join(", ") : "Unknown"} • ${emailPlayer.nationality || 'Unknown'}`,
            timestamp: new Date(email.sent_at),
            iconBgColor: "bg-purple-100",
            iconColor: "text-purple-600",
            icon: "fas fa-envelope"
          });
        }
      });
    }
    
    // Add database update activities - look at most recent player
    if (players.length > 0) {
      // Get the player with the most recent date_added
      const mostRecentPlayer = [...players].sort((a, b) => 
        new Date(b.date_added).getTime() - new Date(a.date_added).getTime()
      )[0];
      
      // Create an activity for database update based on most recent player
      realActivities.push({
        id: "database_update_last",
        type: "database_update",
        title: "Database updated with new players",
        subtitle: `Last update: ${new Date(mostRecentPlayer.date_added).toLocaleDateString()}`,
        timestamp: new Date(mostRecentPlayer.date_added),
        iconBgColor: "bg-yellow-100",
        iconColor: "text-yellow-600",
        icon: "fas fa-sync"
      });
    }
    
    // Sort by timestamp (newest first)
    realActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    // Take only the 5 most recent activities
    return realActivities.slice(0, 5);
  }, [players, shortlists, emails]);
  
  // Function to format time relative to now
  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);
    
    if (diffSec < 60) return "just now";
    if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
    if (diffDay === 1) return "yesterday";
    if (diffDay < 7) return `${diffDay} days ago`;
    
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric" 
    });
  };
  
  if (!activities.length) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-200 lg:col-span-2">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-neutral-800">Recent Activity</h3>
        </div>
        <div className="py-8 text-center text-neutral-500">
          <i className="fas fa-history text-3xl mb-2"></i>
          <p>No recent activity</p>
          <p className="text-sm">Start adding players to see activity here</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-200 lg:col-span-2">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-neutral-800">Recent Activity</h3>
      </div>
      <div className="space-y-4">
        {activities.map(activity => (
          <div key={activity.id} className="flex items-start">
            <div className="flex-shrink-0">
              <span className={`w-8 h-8 rounded-full ${activity.iconBgColor} flex items-center justify-center ${activity.iconColor}`}>
                <i className={`${activity.icon} text-sm`}></i>
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-neutral-800">{activity.title}</p>
              <p className="text-xs text-neutral-500">{activity.subtitle}</p>
              <p className="text-xs text-neutral-400">{getRelativeTime(activity.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
